import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { clientDayKey } from '../../shared/dateTime';
import {
  PURGE_ERROR,
  PURGE_NOTE,
  PURGE_REASON_REQUIRED,
  PURGE_TITLE,
  dayPl,
  fetchPurgePreview,
  purgeUsageEvents,
} from './usageApi';

/**
 * Okno „Usuń dane starsze niż…” (decyzja użytkownika 23.10.2026: „trzymamy,
 * aż usunę” — retencja pomiaru bez limitu, usuwa wyłącznie administrator).
 * Data (dzień polski, najpóźniej dziś), liczba zdarzeń do usunięcia z API,
 * powód obowiązkowy i jawne potwierdzenie. Wpis w dzienniku działań robi API.
 */
export function UsagePurgeDialog({
  onClose,
  onDone,
  now = new Date(),
}: {
  onClose: () => void;
  onDone: (r: { before: string; removed: number }) => void;
  now?: Date;
}) {
  const today = clientDayKey(now);
  const [before, setBefore] = useState('');
  const [reason, setReason] = useState('');
  const [count, setCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setCount(null);
    if (!before) return undefined;
    fetchPurgePreview(before)
      .then((r) => {
        if (alive) setCount(r.count);
      })
      .catch(() => {
        if (alive) setCount(null);
      });
    return () => {
      alive = false;
    };
  }, [before]);

  const confirm = async () => {
    if (reason.trim() === '') {
      setError(PURGE_REASON_REQUIRED);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onDone(await purgeUsageEvents(before, reason.trim()));
    } catch (e) {
      const message = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
      setError(typeof message === 'string' ? message : PURGE_ERROR);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth data-testid="usage-purge-dialog">
      <DialogTitle>{PURGE_TITLE}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {PURGE_NOTE}
        </Alert>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            type="date"
            size="small"
            label="Usuń zdarzenia sprzed dnia"
            value={before}
            onChange={(e) => setBefore(e.target.value)}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { max: today, 'data-testid': 'usage-purge-before' },
            }}
          />
          {before ? (
            <Typography variant="body2" data-testid="usage-purge-count">
              {count === null
                ? 'Liczę zdarzenia…'
                : `Do usunięcia: ${count.toLocaleString('pl-PL')} zdarzeń sprzed ${dayPl(before)}.`}
            </Typography>
          ) : null}
          <TextField
            label="Powód usunięcia"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            multiline
            minRows={2}
            required
            slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'usage-purge-reason' } }}
          />
        </Stack>
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={confirm}
          disabled={busy || !before || before > today || reason.trim() === ''}
          data-testid="usage-purge-confirm"
        >
          Usuń bezpowrotnie
        </Button>
      </DialogActions>
    </Dialog>
  );
}
